import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import { ORG_PROFILE_LABEL, SETUP_STEP_KEYS, type OrgProfile, type SetupCheck, type SetupStatusView, type SetupStepKey } from '@planning/contracts';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { EncabezadoPagina } from '@xiltrion/ui/bloques/encabezado-pagina';
import { EstadoCargando, EstadoError } from '@xiltrion/ui/bloques/estados';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Field, FieldDescription, FieldLabel } from '@xiltrion/ui/field';
import { Icono } from '@xiltrion/ui/icono';
import { Input } from '@xiltrion/ui/input';
import { Item, ItemActions, ItemContent, ItemDescription, ItemGroup, ItemMedia, ItemTitle } from '@xiltrion/ui/item';
import { Progress } from '@xiltrion/ui/progress';
import { RadioGroup, RadioGroupItem } from '@xiltrion/ui/radio-group';
import { avisar } from '@xiltrion/ui/sonner';
import { Spinner } from '@xiltrion/ui/spinner';
import { Stepper, StepperIndicator, StepperItem, StepperNav, StepperSeparator, StepperTitle, StepperTrigger } from '@xiltrion/ui/stepper';
import { apiGet, apiPost } from '@/api/client';
import { avisarError } from '@/api/hooks';
import { useSession, useTerms } from '@/app/session';
import { SettingsAdmin, TeamsAdmin, UsersAdmin } from './Admin';

interface Paso { key: SetupStepKey; titulo: string; checks: SetupCheck[]; faltan: number; avisos: number }

/** El color del indicador sale del estado real del paso (como en SGDEA), no de si va antes o después del actual. */
const INDICADOR = {
  listo: 'data-[state=completed]:bg-success data-[state=completed]:text-success-foreground data-[state=inactive]:bg-success data-[state=inactive]:text-success-foreground',
  aviso: 'data-[state=completed]:bg-warning/15 data-[state=completed]:text-warning data-[state=inactive]:bg-warning/15 data-[state=inactive]:text-warning',
  falta: 'data-[state=completed]:bg-destructive/10 data-[state=completed]:text-destructive data-[state=inactive]:bg-destructive/10 data-[state=inactive]:text-destructive',
  pendiente: 'data-[state=completed]:bg-accent data-[state=completed]:text-accent-foreground',
};

/**
 * Puesta en marcha (spec §4.4), como la de SGDEA: los pasos en orden, cada uno con lo que revisa y la pantalla que lo
 * resuelve; un paso se abre cuando los anteriores cumplen lo obligatorio, y el último pasa la organización a operación.
 * Mientras tanto nadie más entra (`OperationGuard`, `setupGate`). El paso queda en la dirección (`?paso=`).
 */
export function SetupWizard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { refresh } = useSession();
  const { t } = useTerms();
  const [params, setParams] = useSearchParams();
  const q = useQuery({ queryKey: ['setup'], queryFn: () => apiGet<SetupStatusView>('/admin/setup') });
  const [profile, setProfile] = useState<OrgProfile | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  if (q.isError) return <div className="p-6"><EstadoError error={q.error} onReintentar={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="p-6"><EstadoCargando /></div>;

  const st = q.data;
  const titulos: Record<SetupStepKey, string> = { organization: 'Organización', teams: t('team', true), people: 'Personas', settings: 'Vocabulario y calendario', live: 'Pasar a operación' };
  const pasos: Paso[] = SETUP_STEP_KEYS.map((key) => {
    const checks = st.checks.filter((c) => c.step === key);
    return { key, titulo: titulos[key], checks, faltan: checks.filter((c) => !c.ok && c.level === 'required').length, avisos: checks.filter((c) => !c.ok && c.level === 'recommended').length };
  });
  const abierto = (i: number) => pasos.slice(0, i).every((p) => !p.faltan);
  const pedido = SETUP_STEP_KEYS.indexOf(params.get('paso') as SetupStepKey);
  const actual = pedido >= 0 && abierto(pedido) ? pedido : 0;
  const paso = pasos[actual]!;
  const ultimo = actual === pasos.length - 1;
  // Lo que se cambió en un paso (equipos, personas) se vuelve a revisar al moverse.
  const ir = (i: number) => { setParams({ paso: SETUP_STEP_KEYS[i]! }, { replace: true }); void qc.invalidateQueries({ queryKey: ['setup'] }); };
  const listos = pasos.filter((p, i) => !p.faltan && !p.avisos && (p.checks.length > 0 || i < actual) && p.key !== 'live').length;
  const avance = Math.round((listos / (pasos.length - 1)) * 100);

  const perfil = profile ?? st.profile;
  const nombre = name ?? st.entityName;
  const perfilListo = st.checks.some((c) => c.code === 'PROFILE' && c.ok);
  const orgCambio = perfil !== st.profile || (!st.managedBySuite && nombre.trim() !== st.entityName) || !perfilListo;

  const siguiente = async () => {
    // El tipo de organización aplica su vocabulario y sus plantillas: solo si cambió (o nunca se confirmó).
    if (paso.key === 'organization' && orgCambio) {
      setSaving(true);
      try {
        await apiPost('/admin/setup/profile', st.managedBySuite ? { profile: perfil } : { profile: perfil, entityName: nombre.trim() });
        await Promise.all([refresh(), q.refetch()]);
      } catch (e) {
        avisarError(e);
        return;
      } finally {
        setSaving(false);
      }
    }
    ir(actual + 1);
  };

  const pasarAOperacion = async () => {
    try {
      await apiPost('/admin/setup/complete');
    } catch (e) {
      avisarError(e);
      throw e;
    }
    await refresh();
    avisar.success(`${st.entityName} ya está en operación`, { description: 'Las personas de tu organización ya pueden entrar a Planning.' });
    navigate('/');
  };

  const contenido: Record<SetupStepKey, ReactNode> = {
    organization: <PasoOrganizacion st={st} perfil={perfil} nombre={nombre} onPerfil={setProfile} onNombre={setName} />,
    teams: <TeamsAdmin enAsistente />,
    people: <UsersAdmin enAsistente />,
    settings: <SettingsAdmin enAsistente />,
    live: <PasoOperacion st={st} pasos={pasos.slice(0, -1)} onRevisar={ir} />,
  };
  const pendientes = paso.checks.filter((c) => !c.ok);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <EncabezadoPagina titulo="Puesta en marcha" descripcion={`${st.entityName || 'Tu organización'} está en preparación: nadie más puede entrar a Planning hasta que completes estos pasos y la pases a operación. Todo se puede cambiar después en Administración.`} />
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span>Paso {actual + 1} de {pasos.length}</span>
          <span className="tnum text-muted-foreground">{avance}%</span>
        </div>
        <Progress value={avance} aria-label="Avance de la puesta en marcha" />
      </div>

      <div className="grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)]">
        <Stepper value={actual + 1} onValueChange={(n) => { if (abierto(n - 1)) ir(n - 1); }} orientation="vertical" aria-label="Pasos de la puesta en marcha">
          <StepperNav className="w-full">
            {pasos.map((p, i) => {
              const cerrado = !abierto(i);
              const estado = p.faltan ? 'falta' : p.avisos ? 'aviso' : p.key !== 'live' && (p.checks.length > 0 || i < actual) ? 'listo' : 'pendiente';
              // Solo para lectores de pantalla: a la vista, el indicador ya dice si está listo, con avisos o cerrado.
              const texto = cerrado ? 'primero completa los pasos anteriores' : estado === 'listo' ? 'listo' : estado === 'falta' ? 'falta lo obligatorio' : estado === 'aviso' ? 'con recomendaciones' : 'pendiente';
              return (
                <StepperItem key={p.key} step={i + 1} disabled={cerrado} className="items-start">
                  <StepperTrigger className="w-full justify-start rounded-md p-1 text-left hover:bg-muted" aria-label={`Paso ${i + 1}, ${p.titulo}, ${texto}`}>
                    <StepperIndicator className={INDICADOR[estado]}>
                      {estado === 'listo' ? <Icono nombre="mdi:check" className="size-3.5" aria-hidden /> : p.key === 'live' ? <Icono nombre="mdi:flag-outline" className="size-3.5" aria-hidden /> : i + 1}
                    </StepperIndicator>
                    <StepperTitle className="min-w-0 leading-snug">{p.titulo}</StepperTitle>
                  </StepperTrigger>
                  {i < pasos.length - 1 && <StepperSeparator className="ml-[15px] group-data-[orientation=vertical]/stepper-nav:h-3" />}
                </StepperItem>
              );
            })}
          </StepperNav>
        </Stepper>

        <section aria-label={paso.titulo} className="min-w-0 space-y-6">
          {contenido[paso.key]}
          {paso.key !== 'live' && pendientes.length > 0 && <ListaRequisitos checks={pendientes} />}
          <div className="flex items-center justify-between gap-2 border-t border-border pt-4">
            <Button variant="outline" onClick={() => ir(actual - 1)} disabled={actual === 0}><Icono nombre="mdi:arrow-left" /> Anterior</Button>
            {ultimo ? (
              <Button variant="success" disabled={!st.ready} onClick={() => setConfirming(true)}><Icono nombre="mdi:flag-outline" /> Pasar a operación</Button>
            ) : (
              <Button onClick={() => void siguiente()} disabled={saving || (paso.key === 'organization' && !st.managedBySuite && !nombre.trim())}>
                {saving ? <Spinner /> : null} Siguiente <Icono nombre="mdi:arrow-right" />
              </Button>
            )}
          </div>
        </section>
      </div>

      <ConfirmarDialogo
        abierto={confirming}
        onAbiertoChange={setConfirming}
        titulo="¿Pasar a operación?"
        descripcion={`Desde ahora todas las personas de ${st.entityName || 'tu organización'} podrán entrar a Planning y empezar a trabajar. La puesta en marcha no se repite: lo que configuraste se sigue cambiando en Administración.`}
        textoConfirmar="Pasar a operación"
        tono="success"
        icono="mdi:flag-outline"
        onConfirmar={pasarAOperacion}
      />
    </div>
  );
}

/** Paso 1: cómo se llama la organización (si no la administra la suite) y de qué tipo es. */
function PasoOrganizacion({ st, perfil, nombre, onPerfil, onNombre }: { st: SetupStatusView; perfil: OrgProfile; nombre: string; onPerfil: (p: OrgProfile) => void; onNombre: (n: string) => void }) {
  return (
    <div className="space-y-6">
      <EncabezadoPagina titulo="Organización" descripcion="Cómo se llama y qué tipo de organización es. El tipo aplica el vocabulario y las plantillas que le corresponden. Los módulos se encienden en Datalog, en Entidad › Apps y módulos." />
      {st.managedBySuite ? (
        <Alert variant="info"><Icono nombre="mdi:office-building-outline" /><AlertTitle>{st.entityName}</AlertTitle><AlertDescription>El nombre, el NIT y el calendario hábil los administra la suite Datalogic.</AlertDescription></Alert>
      ) : (
        <Field>
          <FieldLabel htmlFor="org">Nombre de la organización</FieldLabel>
          <Input id="org" value={nombre} onChange={(e) => onNombre(e.target.value)} />
        </Field>
      )}
      <Field>
        <FieldLabel>Tipo de organización</FieldLabel>
        <FieldDescription>Si lo cambias después, cambian el vocabulario y las plantillas; los módulos siguen igual.</FieldDescription>
        <RadioGroup value={perfil} onValueChange={(v) => onPerfil(v as OrgProfile)} className="grid gap-3 sm:grid-cols-3">
          {st.profiles.map((x) => (
            <FieldLabel key={x.value} htmlFor={`perfil-${x.value}`} className="cursor-pointer flex-col items-start gap-2 rounded-md border p-4 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5">
              <span className="flex items-center gap-2 font-medium"><RadioGroupItem value={x.value} id={`perfil-${x.value}`} />{ORG_PROFILE_LABEL[x.value]}</span>
            </FieldLabel>
          ))}
        </RadioGroup>
      </Field>
    </div>
  );
}

/** Último paso: lo preparado en cada paso, con lo que falta, antes de abrirle Planning a todos. */
function PasoOperacion({ st, pasos, onRevisar }: { st: SetupStatusView; pasos: Paso[]; onRevisar: (i: number) => void }) {
  return (
    <div className="space-y-6">
      <EncabezadoPagina titulo="Pasar a operación" descripcion={`Revisa lo que preparaste. Al pasar a operación, todas las personas de ${st.entityName || 'tu organización'} podrán entrar a Planning.`} />
      {!st.ready && <Alert variant="destructive"><Icono nombre="mdi:alert-circle-outline" /><AlertTitle>Falta lo obligatorio</AlertTitle><AlertDescription>Completa los pasos marcados en rojo para poder pasar a operación.</AlertDescription></Alert>}
      <ItemGroup className="gap-2">
        {pasos.map((p, i) => {
          const pendientes = p.checks.filter((c) => !c.ok);
          const tono = p.faltan ? 'text-destructive' : p.avisos ? 'text-warning' : 'text-success';
          return (
            <Item key={p.key} variant="outline" size="sm">
              <ItemMedia variant="icon"><Icono nombre={p.faltan ? 'mdi:alert-circle' : p.avisos ? 'mdi:alert-outline' : 'mdi:check-circle'} className={tono} aria-hidden /></ItemMedia>
              <ItemContent>
                <ItemTitle>{i + 1}. {p.titulo}</ItemTitle>
                <ItemDescription className="line-clamp-none">{pendientes.length ? pendientes.map((c) => c.label).join(' · ') : 'Listo'}</ItemDescription>
              </ItemContent>
              <ItemActions>
                {pendientes.length > 0 && <Badge variant={p.faltan ? 'destructive-light' : 'warning-light'}>{p.faltan ? 'Obligatorio' : 'Recomendado'}</Badge>}
                <Button size="sm" variant="ghost" onClick={() => onRevisar(i)}>Revisar</Button>
              </ItemActions>
            </Item>
          );
        })}
      </ItemGroup>
    </div>
  );
}

/** Lo que falta en el paso, con su estado en texto (no solo en color): obligatorio o recomendado. */
function ListaRequisitos({ checks }: { checks: SetupCheck[] }) {
  return (
    <ItemGroup className="gap-2">
      {checks.map((c) => (
        <Item key={c.code} variant="outline" size="sm">
          <ItemMedia variant="icon"><Icono nombre={c.level === 'required' ? 'mdi:alert-circle' : 'mdi:alert-outline'} className={c.level === 'required' ? 'text-destructive' : 'text-warning'} aria-hidden /></ItemMedia>
          <ItemContent><ItemTitle>{c.label}</ItemTitle></ItemContent>
          <ItemActions><Badge variant={c.level === 'required' ? 'destructive-light' : 'warning-light'}>{c.level === 'required' ? 'Obligatorio' : 'Recomendado'}</Badge></ItemActions>
        </Item>
      ))}
    </ItemGroup>
  );
}
